import {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {generatePresentationSlidePreviews} from '../../service/slide.preview';
import {markSlidePreviewsPendingAction, clearSlidePreviewPendingAction} from '../../store/actions/slide.preview';
import {selectedPresentationSelector} from '../../store/selectors/presentation.selectors';

export function useGeneratePresentationSlidePreviews(): {
    generateAll: () => Promise<void>;
    isGenerating: boolean;
} {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const selectedPresentation = useSelector(selectedPresentationSelector);
    const [isGenerating, setIsGenerating] = useState(false);

    const generateAll = useCallback(async (): Promise<void> => {
        const presentationId = selectedPresentation._id;
        const slideIds = (
            selectedPresentation.slideIndexList?.length
                ? selectedPresentation.slideIndexList
                : (selectedPresentation.slides || []).map((slide) => slide._id)
        )
            .map(String)
            .filter(Boolean);
        if (!presentationId || !slideIds.length || isGenerating) {
            return;
        }

        setIsGenerating(true);
        dispatch(markSlidePreviewsPendingAction(slideIds));
        try {
            await generatePresentationSlidePreviews(presentationId);
            toast.success(t('toasts.slidePreviewsQueued'));
        } catch {
            slideIds.forEach((slideId) => dispatch(clearSlidePreviewPendingAction(slideId)));
            toast.error(t('toasts.errorOccurred'));
        } finally {
            setIsGenerating(false);
        }
    }, [dispatch, isGenerating, selectedPresentation._id, selectedPresentation.slideIndexList, selectedPresentation.slides, t]);

    return {generateAll, isGenerating};
}
